import type { ReactNode } from 'react'
import { useId, useState } from 'react'

import { Icon } from './Icon'

/**
 * The folding card the buy/sell frames draw twice — «حالت پیشرفته» on the
 * trade form (`731:219`) and «تنظیمات» on the price alert (`731:11`).
 *
 * Radius 14 on `white/5%` with a `white/9%` edge and 12px padding. The title
 * sits on the right and the chevron on the left, pointing up while the card is
 * open; a `white/8%` rule separates the header from the body. Children are laid
 * out in the same column, so a caller that needs rules between its own
 * sections passes `CardDivider`s between them and they pick up the same gap.
 */
interface CollapsibleCardProps {
  title: string
  /** Drawn beside the title — a status tag such as «غیرفعال». */
  badge?: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  /** The column's rhythm — 12 on the trade form's card, 16 on the alert's. */
  gap?: number
}

export function CollapsibleCard({
  title,
  badge,
  children,
  defaultOpen = true,
  gap = 12,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = useId()

  return (
    <section
      className="flex flex-col overflow-clip rounded-14 border border-line-9 bg-surface-5 p-3"
      style={{ gap }}
    >
      {/* Title first: RTL puts it on the right with the chevron on the left. */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3"
      >
        <span className="flex items-center gap-2">
          <span className="sx-tight text-15 text-ink">{title}</span>
          {badge}
        </span>
        <Icon
          name="chevron-down"
          size={12}
          className={`text-ink transition-transform duration-(--sx-dur) ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <>
          <CardDivider />
          <div id={bodyId} className="flex flex-col" style={{ gap }}>
            {children}
          </div>
        </>
      ) : null}
    </section>
  )
}

/** The `white/8%` hairline between a card's sections. */
export function CardDivider() {
  return <div className="h-px w-full shrink-0 bg-surface-8" aria-hidden="true" />
}
